/** @module A printable list of the collection (or a filtered part), for insurance or a trade. */
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { PageHeader } from "@/components/page-header";
import {
  filterParams,
  filtersOf,
  type FilterSearch,
} from "@/features/collection/collection-filters";
import { variantLabel } from "@/lib/cards";
import { count, day, money } from "@/lib/format";

/** One exported copy; the export answers in plain JSON text fields. */
interface Copy {
  id: string;
  name: string | null;
  set_code: string | null;
  set_id: string | null;
  number: string | null;
  set_total: number | null;
  variant: string | null;
  language: string;
  condition: string;
  value: string | null;
  purchase_price: string | null;
  location: string | null;
}

async function fetchCopies(search: FilterSearch): Promise<Copy[]> {
  const { data, response } = await api.GET("/api/v1/collection/export", {
    params: { query: { ...filterParams(filtersOf(search)), format: "json" } },
    parseAs: "json",
  });
  if (!data) throw problemFrom(response);
  return (data as { items: Copy[] }).items;
}

/** Set code and number, the way the cards sit in a binder. */
function bySetAndNumber(a: Copy, b: Copy): number {
  const set = (a.set_code ?? a.set_id ?? "").localeCompare(b.set_code ?? b.set_id ?? "");
  if (set !== 0) return set;
  return (a.number ?? "").localeCompare(b.number ?? "", undefined, { numeric: true });
}

export function PrintPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const search = useSearch({ strict: false });
  const copies = useQuery({
    queryKey: ["collection-print", search],
    queryFn: () => fetchCopies(search),
  });

  const rows = [...(copies.data ?? [])].sort(bySetAndNumber);
  const total = rows.reduce((sum, row) => sum + Number(row.value ?? 0), 0);
  const unvalued = rows.filter((row) => row.value === null).length;

  return (
    <div className="print-page flex flex-col gap-4">
      <div className="print:hidden">
        <PageHeader
          back
          title={t("transfer.printTitle")}
          actions={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!copies.data}
              onClick={() => {
                window.print();
              }}
            >
              <Printer />
              {t("transfer.printButton")}
            </button>
          }
        />
      </div>
      <div className="hidden print:block">
        <h1 className="t-h2">{t("transfer.printTitle")}</h1>
      </div>
      {copies.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {copies.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {copies.data ? (
        <>
          <p className="t-sm t-muted num">
            {t("transfer.printMeta", {
              date: day(new Date().toISOString(), locale),
              cards: count(rows.length, locale),
              value: money(total, locale),
            })}
            {unvalued > 0 ? ` · ${t("transfer.printUnvalued", { count: unvalued })}` : ""}
          </p>
          <div className="panel overflow-x-auto print:border-0 print:shadow-none">
            <table className="table print-table">
              <thead>
                <tr>
                  <th>{t("collection.card")}</th>
                  <th>{t("collection.set")}</th>
                  <th>{t("collection.variant")}</th>
                  <th>{t("collection.language")}</th>
                  <th>{t("collection.condition")}</th>
                  <th>{t("locations.label")}</th>
                  <th className="td-num">{t("purchase.table.cost")}</th>
                  <th className="td-num">{t("collection.value")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <b>{row.name ?? "–"}</b>
                    </td>
                    <td className="num">
                      {row.set_code ?? row.set_id} {row.number}
                      {row.set_total ? `/${String(row.set_total)}` : ""}
                    </td>
                    <td>
                      {row.variant
                        ? variantLabel(row.variant, (key, fallback) => t(key, fallback))
                        : "–"}
                    </td>
                    <td>{row.language.toUpperCase()}</td>
                    <td className="num">{row.condition}</td>
                    <td>{row.location ?? "–"}</td>
                    <td className="td-num num">{money(row.purchase_price, locale) ?? "–"}</td>
                    <td className="td-num num">
                      {money(row.value, locale) ?? (
                        <span className="t-muted">{t("collection.unvalued")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
